import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { APP_API_URL } from '../../utils';
import UIDropdown from '../../components/ui/ui-dropdown';
import Swal from 'sweetalert2';
import DuoModal from '../../components/ui/duo-modal';

export default function Update({ open, onClose, item, refreshData, storeData, posteData }) {
  const user = useSelector((state) => state.auth.user);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    poste_id: '',
    telephone: '',
    date_embauche: '',
    statut: 'actif',
    store_id: '',
    description: '',
    user_id: user?.id,
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [selectedStore, setSelectedStore] = useState({ label: 'Sélectionner', value: '' });
  const [selectedPoste, setSelectedPoste] = useState({ label: 'Sélectionner', value: '' });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!item) return;

    setFormData({
      name: item.name,
      email: item.email,
      poste_id: item.poste?.id,
      telephone: item.telephone,
      date_embauche: item.date_embauche,
      statut: item.statut,
      store_id: item.store?.id || '',
      description: item.description,
      user_id: user?.id,
      password: '',
    });

    const poste = posteData.find((p) => p.id === item.poste?.id);
    setSelectedPoste({
      label: poste?.titre || item.poste?.titre || 'Sélectionner',
      value: item.poste?.id || '',
    });

    const store = storeData.find((s) => s.id === item.store?.id);
    setSelectedStore({
      label: store?.name || item.store?.name || 'Sélectionner',
      value: item.store?.id || '',
    });

    setShowPassword(false);
    setErrors({});
  }, [item, user?.id, posteData, storeData]);

  if (!open || !item) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleStoreSelect = (option) => {
    setSelectedStore(option);
    setFormData((prev) => ({ ...prev, store_id: option.value }));
  };

  const handlePosteSelect = (option) => {
    setSelectedPoste(option);
    setFormData((prev) => ({ ...prev, poste_id: option.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${APP_API_URL}/personnels/${item.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      await Swal.fire({
        icon: 'success',
        title: 'Succès',
        text: response.data.message || 'Personnel mis à jour avec succès !',
        timer: 2000,
        showConfirmButton: false,
      });

      onClose();
      refreshData();
    } catch (error) {
      if (error.response?.status === 422) {
        setErrors(error.response.data.errors);
        Swal.fire({
          icon: 'error',
          title: 'Erreur de validation',
          text: 'Veuillez vérifier les champs et réessayer.',
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Une erreur est survenue. Veuillez réessayer.',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <DuoModal
      title={`Modifier — ${item.name}`}
      size="lg"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="duo-btn duo-btn--ghost" onClick={onClose} disabled={isLoading}>
            Annuler
          </button>
          <button
            type="submit"
            form={`update-personnel-form-${item.id}`}
            className="duo-btn duo-btn--primary"
            disabled={isLoading}
          >
            {isLoading ? 'Enregistrement...' : 'Enregistrer les modifications'}
          </button>
        </>
      }
    >
      <form id={`update-personnel-form-${item.id}`} onSubmit={handleSubmit}>
        <div className="row g-2">
          <div className="col-md-6">
            <div className="duo-form-group">
              <label htmlFor={`personnel-name-${item.id}`} className="duo-form-label">
                Nom du personnel
              </label>
              <input
                type="text"
                className="duo-input"
                id={`personnel-name-${item.id}`}
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
              {errors.name && <p className="text-danger small mt-1 mb-0">{errors.name[0]}</p>}
            </div>
          </div>
          <div className="col-md-6">
            <div className="duo-form-group">
              <label htmlFor={`personnel-email-${item.id}`} className="duo-form-label">
                Email
              </label>
              <input
                type="email"
                className="duo-input"
                id={`personnel-email-${item.id}`}
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && <p className="text-danger small mt-1 mb-0">{errors.email[0]}</p>}
            </div>
          </div>
          <div className="col-md-6">
            <div className="duo-form-group">
              <label htmlFor={`personnel-telephone-${item.id}`} className="duo-form-label">
                Téléphone
              </label>
              <input
                type="text"
                className="duo-input"
                id={`personnel-telephone-${item.id}`}
                name="telephone"
                value={formData.telephone}
                onChange={handleChange}
              />
              {errors.telephone && <p className="text-danger small mt-1 mb-0">{errors.telephone[0]}</p>}
            </div>
          </div>
          <div className="col-md-6">
            <div className="duo-form-group">
              <label className="duo-form-label">Choisir un poste</label>
              <UIDropdown
                options={posteData.map((p) => ({ label: p.titre, value: p.id }))}
                selectedOption={selectedPoste}
                onSelect={handlePosteSelect}
                enableSearch={true}
              />
              {errors.poste && <p className="text-danger small mt-1 mb-0">{errors.poste[0]}</p>}
              {errors.poste_id && <p className="text-danger small mt-1 mb-0">{errors.poste_id[0]}</p>}
            </div>
          </div>
          <div className="col-md-6">
            <div className="duo-form-group">
              <label className="duo-form-label">Choisir une boutique</label>
              <UIDropdown
                options={storeData.map((s) => ({ label: s.name, value: s.id }))}
                selectedOption={selectedStore}
                onSelect={handleStoreSelect}
                enableSearch={true}
              />
              {errors.store_id && <p className="text-danger small mt-1 mb-0">{errors.store_id[0]}</p>}
            </div>
          </div>
          <div className="col-md-6">
            <div className="duo-form-group">
              <label htmlFor={`personnel-password-${item.id}`} className="duo-form-label">
                Mot de passe
              </label>
              <div className="d-flex gap-2">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="duo-input"
                  id={`personnel-password-${item.id}`}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="duo-btn duo-btn--ghost"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? 'Masquer' : 'Afficher'}
                </button>
              </div>
              {errors.password && <p className="text-danger small mt-1 mb-0">{errors.password[0]}</p>}
            </div>
          </div>
          <div className="col-12">
            <div className="duo-form-group mb-0">
              <label className="duo-form-label d-flex align-items-start gap-2">
                <input
                  type="checkbox"
                  className="form-check-input mt-1"
                  name="statut"
                  checked={formData.statut === 'actif'}
                  onChange={(e) =>
                    handleChange({ target: { name: 'statut', value: e.target.checked ? 'actif' : 'inactif' } })
                  }
                />
                <span>
                  Statut actif
                  <span className="d-block fw-normal" style={{ fontSize: '0.78rem', color: '#777' }}>
                    Décochez cette case si vous souhaitez que ce personnel soit inactif.
                  </span>
                </span>
              </label>
              {errors.statut && <p className="text-danger small mt-1 mb-0">{errors.statut[0]}</p>}
            </div>
          </div>
        </div>
      </form>
    </DuoModal>
  );
}
